import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { catchMarkerIcon } from '../lib/leafletIcon';
import { getCatch, fetchCatchPhotoObjectUrl } from '../api/catches';
import type { CatchDetailResponse } from '../types/api';
import { CatchWeatherCard } from '../components/catches/CatchWeatherCard';

export function CatchDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<CatchDetailResponse | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    getCatch(id)
      .then(async (d) => {
        if (cancelled) return;
        setData(d);
        if (d.photoUrl) {
          const url = await fetchCatchPhotoObjectUrl(d.id);
          if (cancelled) { if (url) URL.revokeObjectURL(url); return; }
          objectUrl = url;
          setPhotoUrl(url);
        }
      })
      .catch(() => { if (!cancelled) setError('Failed to load catch.'); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [id]);

  if (error) return (
    <div className="max-w-2xl mx-auto space-y-4">
      <button onClick={() => navigate(-1)} className="text-sm text-teal-600 dark:text-teal-400 hover:underline cursor-pointer">← Back</button>
      <p className="text-sm text-red-500">{error}</p>
    </div>
  );
  if (!data) return <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <button
        onClick={() => navigate(-1)}
        className="text-sm text-teal-600 dark:text-teal-400 hover:underline cursor-pointer"
      >
        ← Back
      </button>

      {photoUrl && (
        <img src={photoUrl} alt={data.fishTypeName}
             className="w-full max-h-96 rounded-xl border border-slate-200 dark:border-slate-700 object-cover" />
      )}

      <div className="space-y-1">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">
          {data.fishTypeName}{data.lengthInches != null ? ` · ${data.lengthInches}"` : ''}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {data.spotName ?? 'No spot'} · {new Date(data.caughtAt).toLocaleString()}
          {data.lureTypeName ? ` · ${data.lureTypeName}` : ''}
        </p>
      </div>

      <div className="relative z-0 h-56 w-full rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600">
        <MapContainer center={[data.latitude, data.longitude]} zoom={13} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          <Marker position={[data.latitude, data.longitude]} icon={catchMarkerIcon} />
        </MapContainer>
      </div>

      {data.weatherStatus === 'Resolved' && data.weather
        ? <CatchWeatherCard weather={data.weather} />
        : (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-5 text-sm text-slate-500 dark:text-slate-400">
            Weather snapshot is pending — recent catches resolve once historical data is available.
            Reopen this catch later to see it.
          </div>
        )}

      {data.notes && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
          <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{data.notes}</p>
        </div>
      )}
    </div>
  );
}
